import type { ComponentType, SVGProps } from 'react'
import type { TI18nKey } from '@muse/i18n'
import {
    AcousticGuitarIcon,
    AudioToTextIcon,
    CodeIcon,
    DonationIcon,
    ElectricGuitarIcon,
    RemoveVocalIcon,
    ReplaceIcon,
    ScissorsIcon,
    StatisticsIcon,
    VerifiedIcon,
    VideoIcon,
    VocalIcon,
} from '@ds/components'
import type { THeaderNavLinkEventName, THeaderNavSectionEventName } from '@core/analytics'
import type { TSeoNavEntry, TSeoNavLink } from '../components/types'
import { ROUTES } from './routes'

/**
 * The header's own i18n keys, validated via `TI18nKey` (a typo or a removed key is a compile error).
 * Enumerated rather than derived from `t()`'s signature: that signature became a conditional tuple
 * once the first key with ICU params landed, and `Parameters<>` of it collapses to `never`.
 */
export type TTranslationKey = TI18nKey<
    | 'i18n.commonMp3Cutter'
    | 'i18n.headerMp3CutterDesc'
    | 'i18n.commonAnalytics'
    | 'i18n.commonApi'
    | 'i18n.commonInsights'
    | 'i18n.commonInstrumentalExtractor'
    | 'i18n.headerApiDesc'
    | 'i18n.headerAudacity'
    | 'i18n.headerAudioConverter'
    | 'i18n.headerAudioConverterDesc'
    | 'i18n.headerAudioTools'
    | 'i18n.headerCatalog'
    | 'i18n.headerCollections'
    | 'i18n.headerCreatorTools'
    | 'i18n.headerEarn'
    | 'i18n.headerEarnDesc'
    | 'i18n.headerFeed'
    | 'i18n.headerGenres'
    | 'i18n.headerGetPro'
    | 'i18n.headerHome'
    | 'i18n.headerInsightsDesc'
    | 'i18n.headerInstrumentalExtractorDesc'
    | 'i18n.headerListen'
    | 'i18n.headerLogIn'
    | 'i18n.headerLogOut'
    | 'i18n.headerMore'
    | 'i18n.headerMyAudio'
    | 'i18n.headerNew'
    | 'i18n.headerOpenMenu'
    | 'i18n.headerProjects'
    | 'i18n.headerPromoPages'
    | 'i18n.headerPromoPagesDesc'
    | 'i18n.headerSearch'
    | 'i18n.headerSettings'
    | 'i18n.headerSoundEffects'
    | 'i18n.headerSpeechToText'
    | 'i18n.headerSpeechToTextDesc'
    | 'i18n.headerStemSplitter'
    | 'i18n.headerStemSplitterDesc'
    | 'i18n.headerSubmitSearch'
    | 'i18n.headerTryFree'
    | 'i18n.headerUpload'
    | 'i18n.headerVideoToMp3'
    | 'i18n.headerVideoToMp3Desc'
    | 'i18n.headerVocalRemover'
    | 'i18n.headerVocalRemoverDesc'
    | 'i18n.headerVoiceExtractor'
    | 'i18n.headerVoiceExtractorDesc'
>

// `label`/`description` feed the SSR templates (no i18n at build time); `labelKey`/`descriptionKey`
// are resolved via t() at runtime.
export type TNavLink = {
    labelKey: TTranslationKey
    label: string
    href: string
    Icon?: ComponentType<SVGProps<SVGSVGElement>>
    descriptionKey?: TTranslationKey
    description?: string
    isNew?: boolean
    analytics?: THeaderNavLinkEventName
}

/**
 * `panel` is the shape audio-web hardcodes per dropdown: `list` is the narrow 178px column (Listen),
 * `grid` is the full-bleed feature panel that spans the header and lays its cells out in 20rem columns.
 */
export type TNavDropdown = {
    labelKey: TTranslationKey
    label: string
    links: readonly TNavLink[]
    panel: 'list' | 'grid'
    analytics?: THeaderNavSectionEventName
}

export const LISTEN_LINKS = [
    {
        labelKey: 'i18n.headerFeed',
        label: 'Feed',
        href: ROUTES.feed,
        analytics: 'header_feed_click',
    },
    {
        labelKey: 'i18n.headerGenres',
        label: 'Genres',
        href: ROUTES.genres,
        analytics: 'header_genres_click',
    },
    {
        labelKey: 'i18n.headerSoundEffects',
        label: 'Sound Effects',
        href: ROUTES.soundEffects,
        analytics: 'header_sound_effects_click',
    },
    {
        labelKey: 'i18n.headerCatalog',
        label: 'Catalog',
        href: ROUTES.catalog,
        analytics: 'header_search_click',
    },
] satisfies TNavLink[]

export const AUDIO_TOOLS_LINKS = [
    {
        labelKey: 'i18n.headerSpeechToText',
        label: 'Speech to text',
        href: ROUTES.toolTranscription,
        Icon: AudioToTextIcon,
        descriptionKey: 'i18n.headerSpeechToTextDesc',
        description:
            'AI-powered voice recognition and online transcription gives you more time to focus on your music, podcasts, and audio projects.',
        analytics: 'header_transcribe_click',
    },
    {
        labelKey: 'i18n.headerAudioConverter',
        label: 'Audio converter',
        href: ROUTES.toolAudioConverter,
        Icon: ReplaceIcon,
        descriptionKey: 'i18n.headerAudioConverterDesc',
        description:
            'Support for all formats. Process your files quickly, with no installation required.',
        analytics: 'header_converter_click',
    },
    {
        labelKey: 'i18n.headerVideoToMp3',
        label: 'Video to MP3',
        href: ROUTES.toolVideoToMp3,
        Icon: VideoIcon,
        descriptionKey: 'i18n.headerVideoToMp3Desc',
        description:
            'Extract the audio track from MP4, MOV, AVI, WEBM, MKV and other video files and download it as MP3.',
        analytics: 'header_video_to_mp3_click',
    },
    {
        labelKey: 'i18n.commonMp3Cutter',
        label: 'MP3 Cutter',
        href: ROUTES.toolMp3Cutter,
        Icon: ScissorsIcon,
        descriptionKey: 'i18n.headerMp3CutterDesc',
        description: 'Trim or cut any audio file right in your browser.',
        isNew: true,
        analytics: 'header_cutter_click',
    },
    {
        labelKey: 'i18n.headerVocalRemover',
        label: 'AI Vocal Remover',
        href: ROUTES.toolVocalRemover,
        Icon: RemoveVocalIcon,
        descriptionKey: 'i18n.headerVocalRemoverDesc',
        description:
            'Isolate instrumentals from any music track. Create karaoke tracks, remixes, or backing tracks easily.',
        analytics: 'header_vremover_click',
    },
    {
        labelKey: 'i18n.headerStemSplitter',
        label: 'AI Stem Splitter',
        href: ROUTES.toolStemSplitter,
        Icon: ElectricGuitarIcon,
        descriptionKey: 'i18n.headerStemSplitterDesc',
        description: 'Isolate vocals, drums, bass, and instruments from any song.',
        analytics: 'header_splitter_click',
    },
    {
        labelKey: 'i18n.headerVoiceExtractor',
        label: 'Voice Extractor',
        href: ROUTES.toolVoiceExtractor,
        Icon: VocalIcon,
        descriptionKey: 'i18n.headerVoiceExtractorDesc',
        description:
            'Remove instruments and background sounds from any audio or song, leaving just the vocal track.',
        analytics: 'header_vextractor_click',
    },
    {
        labelKey: 'i18n.commonInstrumentalExtractor',
        label: 'Instrumental Extractor',
        href: ROUTES.toolInstrumentalExtractor,
        Icon: AcousticGuitarIcon,
        descriptionKey: 'i18n.headerInstrumentalExtractorDesc',
        description:
            'Create an instrumental version of a song with AI. Use when the backing track is the result you want.',
        analytics: 'header_iextractor_click',
    },
] satisfies TNavLink[]

const INSIGHTS_LINK = {
    labelKey: 'i18n.commonInsights',
    label: 'Insights',
    href: ROUTES.insights,
    Icon: StatisticsIcon,
    descriptionKey: 'i18n.headerInsightsDesc',
    description: 'Track plays, engagement, audience growth, and performance across your audio.',
    isNew: true,
    analytics: 'header_insights_click',
} satisfies TNavLink

export const CREATOR_TOOLS_LINKS = [
    {
        labelKey: 'i18n.commonApi',
        label: 'API',
        href: ROUTES.apiLanding,
        Icon: CodeIcon,
        descriptionKey: 'i18n.headerApiDesc',
        description:
            'Upload tracks, organize them into collections, search across content, and generate ready-to-paste players.',
        analytics: 'header_api_click',
    },
    {
        labelKey: 'i18n.headerEarn',
        label: 'Earn with audio.com',
        href: ROUTES.fanSupport,
        Icon: DonationIcon,
        descriptionKey: 'i18n.headerEarnDesc',
        description:
            'Showcase your work, tell your story, and give your fans a place to connect and support you — all in just a few clicks.',
        analytics: 'header_earn_click',
    },
    {
        labelKey: 'i18n.headerPromoPages',
        label: 'Promo Pages',
        href: ROUTES.promoPages,
        Icon: VerifiedIcon,
        descriptionKey: 'i18n.headerPromoPagesDesc',
        description:
            'Showcase your tracks, highlight who you are, and make it easy for fans to stay connected — all through a single link.',
        analytics: 'header_promo_pages_click',
    },
    INSIGHTS_LINK,
] satisfies TNavLink[]

export const AUDACITY_LINK = {
    labelKey: 'i18n.headerAudacity',
    label: 'Audacity',
    href: ROUTES.audacityDownload,
    analytics: 'header_au_click',
} satisfies TNavLink

export const GET_PRO_LINK = {
    labelKey: 'i18n.headerGetPro',
    label: 'Get Pro',
    href: ROUTES.subscriptions,
    analytics: 'header_pro_click',
} satisfies TNavLink

export const LISTEN_DROPDOWN = {
    labelKey: 'i18n.headerListen',
    label: 'Listen',
    links: LISTEN_LINKS,
    panel: 'list',
    analytics: 'header_listen_click',
} satisfies TNavDropdown
export const AUDIO_TOOLS_DROPDOWN = {
    labelKey: 'i18n.headerAudioTools',
    label: 'Audio Tools',
    links: AUDIO_TOOLS_LINKS,
    panel: 'grid',
    analytics: 'header_tools_click',
} satisfies TNavDropdown
export const CREATOR_TOOLS_DROPDOWN = {
    labelKey: 'i18n.headerCreatorTools',
    label: 'Creator Tools',
    links: CREATOR_TOOLS_LINKS,
    panel: 'grid',
    analytics: 'header_creator_tools_click',
} satisfies TNavDropdown

// `collapsible` entries (Listen, Audacity) fold into the "More" (…) menu at 1280–1439, mirroring prod.
/** Stable identity of a top-level entry — the key the backend names when it marks one active. */
export type TNavKey = 'listen' | 'audacity' | 'audioTools' | 'creatorTools' | 'getPro'

export type TNavEntry =
    | { kind: 'dropdown'; navKey: TNavKey; dropdown: TNavDropdown; collapsible?: boolean }
    | { kind: 'link'; navKey: TNavKey; link: TNavLink; isBrand?: boolean; collapsible?: boolean }

// Annotated (not `satisfies`): the container reads optional `Icon`/`isNew` off the links, which
// requires the widened `TNavLink` interface rather than the leaves' concrete (optional-less) types.
export const NAV_ENTRIES: TNavEntry[] = [
    { kind: 'dropdown', navKey: 'listen', dropdown: LISTEN_DROPDOWN, collapsible: true },
    { kind: 'link', navKey: 'audacity', link: AUDACITY_LINK, collapsible: true },
    { kind: 'dropdown', navKey: 'audioTools', dropdown: AUDIO_TOOLS_DROPDOWN },
    { kind: 'dropdown', navKey: 'creatorTools', dropdown: CREATOR_TOOLS_DROPDOWN },
    { kind: 'link', navKey: 'getPro', link: GET_PRO_LINK, isBrand: true },
]

export const SEO_NAV_LABEL = 'SEO navigation'

// audio-web hardcodes both nav labels in English rather than translating them; kept 1:1 so the
// SSR template and the hydrated header carry the same accessible name.
export const MAIN_NAV_LABEL = 'Main navigation'

// SSR SEO navigation (sr-only, nested), mirroring audio-web's hidden crawler nav. Note the Listen
// group here is a curated subset (Catalog/Genres/Sound Effects — no Feed), matching prod.
const SEO_LISTEN_LINKS: TSeoNavLink[] = [
    { label: 'Catalog', href: ROUTES.catalog },
    { label: 'Genres', href: ROUTES.genres },
    { label: 'Sound Effects', href: ROUTES.soundEffects },
]

const toSeoLinks = (links: readonly TNavLink[]): TSeoNavLink[] =>
    links.map(link => ({ label: link.label, href: link.href, description: link.description }))

export const SEO_NAV: TSeoNavEntry[] = [
    { kind: 'group', label: 'Listen', links: SEO_LISTEN_LINKS },
    { kind: 'link', label: AUDACITY_LINK.label, href: AUDACITY_LINK.href },
    { kind: 'group', label: 'Audio Tools', links: toSeoLinks(AUDIO_TOOLS_LINKS) },
    { kind: 'group', label: 'Creator Tools', links: toSeoLinks(CREATOR_TOOLS_LINKS) },
    { kind: 'link', label: GET_PRO_LINK.label, href: GET_PRO_LINK.href },
]
